import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerSW } from "virtual:pwa-register";

import { Button } from "@/components/ui/button";
import { Sentry } from "@/lib/sentry";

/** How often an app left open looks for a newer release. */
const CHECK_EVERY_MS = 30 * 60 * 1000;

/**
 * Says when a newer release is ready, and takes it on request.
 *
 * The new version waits until asked: taking it reloads the page, which would
 * throw away a form being filled in. An installed app is resumed far more often
 * than it is loaded, so it also looks for a release each time it comes back to
 * the front, not only on load.
 */
export function UpdateNotice() {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  const apply = useRef<() => Promise<void>>(undefined);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    let registration: ServiceWorkerRegistration | undefined;
    const check = () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      registration?.update().catch(() => {
        // Offline or a failed fetch: the next check will do.
      });
    };

    const update = registerSW({
      onNeedRefresh: () => setReady(true),
      onRegisteredSW: (_url, r) => {
        registration = r;
      },
      onRegisterError: (error: unknown) => Sentry.captureException(error),
    });
    apply.current = () => update(true);

    document.addEventListener("visibilitychange", check);
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.clearInterval(timer);
    };
  }, []);

  if (!ready) return null;

  return (
    <div
      role="status"
      className="pointer-events-none fixed inset-x-0 top-[max(0.75rem,env(safe-area-inset-top))] z-[60] flex justify-center px-4"
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-md border border-line bg-paper py-2 ps-4 pe-2 text-sm text-ink shadow-[0_18px_40px_-18px_rgb(0_0_0/0.25),0_2px_6px_-2px_rgb(0_0_0/0.06)]">
        <span>{t("update.ready")}</span>
        <Button
          size="sm"
          disabled={reloading}
          onClick={() => {
            setReloading(true);
            void apply.current?.();
          }}
        >
          {t("update.reload")}
        </Button>
      </div>
    </div>
  );
}
